// Turn raw audit-trail rows (migration 0008: INSERT/DELETE keep the whole row, UPDATE keeps only
// {column: [old, new]}) into plain words for the History screen. Pure: no network, no React.
import { formatDay } from './format'
import { formatKwacha } from './money'

export type AuditOp = 'INSERT' | 'UPDATE' | 'DELETE'

/** What each kind of record is called on screen. Tables not listed fall back to their name in words. */
export const RECORD_KINDS: Record<string, string> = {
  department: 'department',
  app_user: 'account',
  user_role: 'role',
  app_owner: 'owner of the app',
  church_setting: 'church settings',
  approval_policy: 'approval rule',
  fiscal_quarter: 'quarter',
  member: 'member',
  department_membership: 'department membership',
  dues_plan: 'dues plan',
  dues_enrollment: 'dues enrolment',
  dues_charge: 'dues charge',
  dues_allocation: 'dues payment applied to a charge',
  project: 'project',
  collection_sheet: 'collection sheet',
  contribution: 'payment',
  expense_category: 'expense category',
  budget: 'budget',
  budget_line: 'budget line',
  expense: 'expense',
  cash_movement: 'remittance',
  cash_movement_ack: 'record of money received',
  cash_movement_match: 'reconciliation match',
  reconciliation_exception: 'reconciliation flag',
  attachment: 'photo',
  income_fund: 'church income fund',
  income_session: 'counted service',
  income_line: 'church income amount',
  income_transfer: 'transfer to the Conference',
}

const words = (s: string) => s.replace(/_/g, ' ')
export const kindOf = (table: string) => RECORD_KINDS[table] ?? words(table)

export const OP_VERBS: Record<AuditOp, string> = { INSERT: 'added', UPDATE: 'changed', DELETE: 'deleted' }

/** Bookkeeping columns nobody needs to read: ids, retry keys, columns the database works out itself. */
const HIDDEN = new Set([
  'id', 'department_id', 'created_at', 'created_by', 'updated_at', 'updated_by', 'client_ref', 'request_hash',
  'quarter_id', 'period_month', 'is_unbudgeted', 'bank_ref_norm', 'revision', 'bucket', 'storage_path', 'thumb_path',
  'size_bytes', 'mime_type', 'txid',
])

/** Column names that read badly when simply un-underscored. */
const FIELD_NAMES: Record<string, string> = {
  amount_ngwee: 'Amount',
  declared_amount_ngwee: 'Amount sent',
  received_amount_ngwee: 'Amount received',
  target_ngwee: 'Target',
  expense_threshold_ngwee: 'Approval threshold',
  dept_cash_float_limit_ngwee: 'Cash a department may hold',
  full_name: 'Name',
  received_on: 'Date received',
  incurred_on: 'Date spent',
  paid_on: 'Date paid',
  moved_on: 'Date sent',
  sent_on: 'Date sent',
  service_on: 'Service date',
  sheet_on: 'Sheet date',
  starts_on: 'Starts',
  ends_on: 'Ends',
  effective_from: 'Starts',
  effective_to: 'Ends',
  effective_on: 'Date',
  archived_at: 'Archived',
  closed_at: 'Closed',
  revoked_at: 'Removed',
  cancelled_at: 'Cancelled',
  reverses_id: 'Reverses an earlier entry',
  reversal_reason: 'Reason for reversal',
  cancel_reason: 'Reason for cancelling',
  decision_note: 'Decision note',
  must_change_password: 'Must change password',
  password_changed_at: 'Password changed',
  is_active: 'Account switched on',
  is_central: 'Central treasury',
  receipt_no: 'Receipt number',
  slip_no: 'Slip number',
  bank_ref: 'Bank reference',
  budget_line_id: 'Budget line',
  category_id: 'Category',
  member_id: 'Member',
  project_id: 'Project',
  sheet_id: 'Collection sheet',
  movement_id: 'Remittance',
  approval_policy_id: 'Approval rule',
  user_id: 'Person',
}

export function fieldName(col: string): string {
  if (FIELD_NAMES[col]) return FIELD_NAMES[col]
  const base = words(col.replace(/_(ngwee|id)$/, '').replace(/_(on|at)$/, ''))
  return base.charAt(0).toUpperCase() + base.slice(1)
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const DATE = /^\d{4}-\d{2}-\d{2}$/
const STAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/
const WORD_COLUMNS = new Set(['status', 'method', 'role', 'kind', 'mode', 'direction', 'cadence', 'approved_via', 'paid_from', 'destination'])

const lusakaTime = new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Lusaka', hour: '2-digit', minute: '2-digit', hour12: false })
const lusakaDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Lusaka', year: 'numeric', month: '2-digit', day: '2-digit' })

/** An event moment shown in Lusaka time: '7 Oct 2026, 14:05'. */
export function formatMoment(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${formatDay(lusakaDate.format(d))}, ${lusakaTime.format(d)}`
}

/**
 * One value in words. A link to another record (an id) returns null: an id means nothing to a person,
 * so the screen says only that the link changed.
 */
export function formatValue(col: string, v: unknown): string | null {
  if (v === null || v === undefined || v === '') return '(blank)'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (col.endsWith('_ngwee') && typeof v === 'number' && Number.isInteger(v)) return formatKwacha(v)
  if (typeof v === 'string') {
    if (UUID.test(v)) return null
    if (DATE.test(v)) return formatDay(v)
    if (STAMP.test(v)) return formatMoment(v)
    if (WORD_COLUMNS.has(col)) return words(v)
    return v.length > 160 ? `${v.slice(0, 157)}...` : v
  }
  if (typeof v === 'number') return String(v)
  return JSON.stringify(v).slice(0, 160)
}

export interface FieldChange {
  field: string
  /** Both null when the value is a link to another record: the screen says only "changed". */
  before: string | null
  after: string | null
}

/** The changes worth showing for one audit row. */
export function describeChanges(op: AuditOp, changes: Record<string, unknown> | null | undefined): FieldChange[] {
  const out: FieldChange[] = []
  for (const [col, raw] of Object.entries(changes ?? {})) {
    if (HIDDEN.has(col)) continue
    if (op === 'UPDATE') {
      const [b, a] = Array.isArray(raw) ? raw : [undefined, raw]
      const fb = formatValue(col, b)
      const fa = formatValue(col, a)
      const isLink = (fb === null && fa === null) || (fb === null && fa !== '(blank)') || (fa === null && fb !== '(blank)')
      if (isLink) out.push({ field: fieldName(col), before: null, after: null })
      else out.push({ field: fieldName(col), before: fb ?? 'a linked record', after: fa ?? 'a linked record' })
    } else {
      if (raw === null || raw === undefined || raw === '' || raw === false) continue // an empty field is not news
      const f = formatValue(col, raw)
      if (f === null) continue
      out.push(op === 'INSERT' ? { field: fieldName(col), before: null, after: f } : { field: fieldName(col), before: f, after: null })
    }
  }
  return out
}

/** A short name for a record, from its full row as stored when it was added: 'Mary Banda', 'Fuel, K120.00'. */
export function recordLabel(table: string, row: Record<string, unknown> | null | undefined): string | null {
  if (!row) return null
  const str = (k: string) => (typeof row[k] === 'string' && (row[k] as string).trim() ? (row[k] as string).trim() : null)
  const money = ['amount_ngwee', 'declared_amount_ngwee', 'received_amount_ngwee', 'target_ngwee']
    .map((k) => row[k])
    .find((v): v is number => typeof v === 'number' && Number.isInteger(v))
  const day = ['received_on', 'incurred_on', 'moved_on', 'sent_on', 'service_on', 'sheet_on', 'effective_from', 'effective_on']
    .map(str)
    .find((v): v is string => v !== null && DATE.test(v))

  let name: string | null
  if (table === 'budget') name = str('quarter_id') ? `Budget for ${str('quarter_id')}` : null
  else if (table === 'user_role' || table === 'approval_policy') name = str('role') ?? str('mode') ? words((str('role') ?? str('mode'))!) : null
  else if (table === 'church_setting') name = 'Church settings'
  else name = str('full_name') ?? str('name') ?? str('label') ?? str('payee') ?? str('description') ?? str('caption') ?? str('code')

  const parts = [name, money !== undefined ? formatKwacha(money) : null, day ? formatDay(day) : null].filter(Boolean)
  return parts.length ? parts.join(', ') : null
}
